import styled from '@emotion/styled';
import _ from 'lodash';
import { createElement, FC, useEffect } from 'react';

import { CreationState } from '../../../graphql/generated/graphql';
import { useGetOfferQuery } from '../../../hooks/useGetOfferQuery';
import { useGetProjectQuery } from '../../../hooks/useGetProjectQuery';
import { toJSDate } from '../../../utility/graphql/datetime';
import Spinner from '../../component/compound/Spinner';
import { SPACING_UNIT } from '../../constant/spacing';
import CreationContent from './CreationContent';
import useGetCreationQuery from './useGetCreationQuery';
import useGetModificationRequestAssetsQuery from './useGetModificationRequestAssetsQuery';

interface Props {
  creationId: string;
  selectedModificationRequestIssueId: string;
  selectedModificationRequestId: string;
  className?: string;
  language: string;
  prepareProject: (projectId: string) => Promise<void>;
}

const ShowCreationPage: FC<Props> = ({
  creationId,
  selectedModificationRequestIssueId,
  selectedModificationRequestId,
  className,
  language,
  prepareProject,
}) => {
  const { creation, reload, loading } = useGetCreationQuery(creationId);
  const { project, briefFiles, assetFiles, loading: loadingProject } = useGetProjectQuery(creation?.creationSetId);
  const {
    assetFiles: modificationRequestAssetFiles,
    loading: loadingModificationRequest,
  } = useGetModificationRequestAssetsQuery(creation?.modificationRequests || []);
  useEffect(() => {
    // In-Page Link
    if (!loadingModificationRequest) {
      const targetElement = document.getElementById(
        selectedModificationRequestIssueId
          ? `modificationRequestIssue-${selectedModificationRequestIssueId}`
          : selectedModificationRequestId
          ? 'modificationRequest'
          : '',
      );

      if (targetElement) {
        const rectTop = targetElement.getBoundingClientRect().top;
        const offsetTop = window.pageYOffset;
        const headBarHeight = 60;
        const top = rectTop + offsetTop - headBarHeight;

        window.scrollTo({
          top,
          behavior: 'smooth',
        });
      }
    }
  }, [loadingModificationRequest, selectedModificationRequestId, selectedModificationRequestIssueId]);
  const { offer, loading: loadingOffer } = useGetOfferQuery(project?.creationSet?.offerId || undefined);

  useEffect(() => {
    if (project?.id != null) {
      prepareProject(project.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  if (!creation || !project || !offer) {
    return (
      <Root className={className}>
        <Spinner />
      </Root>
    );
  }

  const creationSet = project.creationSet!;
  // 修正依頼差し戻し -> 再度修正依頼時に過去分は表示しない
  const modificationRequests = creation.modificationRequests
    .filter(
      (modificationRequest, index, modificationRequests) =>
        _.findLastIndex(
          modificationRequests,
          (v2) => modificationRequest.targetDeliverableId === v2.targetDeliverableId,
        ) === index,
    )
    .sort((a, b) => toJSDate(b.submittedAt).valueOf() - toJSDate(a.submittedAt).valueOf());

  const panelExpanded = {
    creativeBrief: creation.deliverables.length === 0,
    submission: modificationRequests.length === 0,
    modification: creation.deliverables.length > 0 || modificationRequests.length > 0,
  };
  const hideSubmissionForm = creation.state === CreationState.Canceled;

  return (
    <CreationContent
      project={project}
      paramSelectedModificationRequestId={selectedModificationRequestId}
      creation={creation}
      assetFiles={assetFiles}
      briefFiles={briefFiles}
      loading={loading || loadingProject || loadingModificationRequest || loadingOffer}
      modificationRequestAssets={modificationRequestAssetFiles}
      hideSubmissionForm={hideSubmissionForm}
      panelExpanded={panelExpanded}
      targetCreationSet={creationSet}
      offer={offer}
      modificationRequests={modificationRequests}
      refresh={reload}
      language={language}
    />
  );
};

const Root = styled('div')({
  margin: `0 ${SPACING_UNIT}px`,
  padding: `${SPACING_UNIT * 5}px calc((100% - 960px) / 2) ${SPACING_UNIT * 10}px`,
});

export default ShowCreationPage;
